import React, { useState, useEffect } from "react";
import { Modal, Button, Upload, message } from "antd"; // Import Ant Design components
import {
	CameraOutlined,
	UploadOutlined,
	UserOutlined,
} from "@ant-design/icons"; // Import camera icon
import profileImage from "../../../assets/image/propic.jpg"; // Fallback image
import {
	updateUserProfile,
	uploadProfileImage,
} from "../../../services/profile/profileService"; // Importing both functions

function ProfileHeader({
	name,
	email,
	phone,
	profilePic,
	userId, // Accept userId as a prop
	onProfileUpdated, // Callback to notify parent component of update
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [updatedName, setUpdatedName] = useState(name || "");
	const [updatedEmail, setUpdatedEmail] = useState(email || "");
	const [updatedPhone, setUpdatedPhone] = useState(phone || "");
	const [updatedProfilePicture, setUpdatedProfilePicture] =
		useState(profilePic);
	const [loading, setLoading] = useState(false); // Loading state
	const [isModalVisible, setIsModalVisible] = useState(false); // Modal visibility state
	const [file, setFile] = useState(null); // State for file input

	// Effect to update profile picture if prop changes (e.g., after upload)
	useEffect(() => {
		setUpdatedProfilePicture(profilePic);
	}, [profilePic]);

	const handleSave = async () => {
		try {
			setLoading(true); // Start loading
			const updatedProfileData = {
				fullName: updatedName,
				email: updatedEmail,
				phone: updatedPhone,
			};
			// Call the service to update the user profile
			await updateUserProfile(userId, updatedProfileData);

			// Notify parent component of the profile update
			onProfileUpdated({
				name: updatedName,
				email: updatedEmail,
				phone: updatedPhone,
				profilePicture: updatedProfilePicture,
			});

			setIsEditing(false);
		} catch (error) {
			console.error("Error updating profile:", error);
		} finally {
			setLoading(false); // Stop loading
		}
	};

	// const handleFileChange = (event) => {
	//   const selectedFile = event.target.files[0];
	//   if (selectedFile) {
	//     setFile(selectedFile);
	//   }
	// };

	const handleFileChange = ({ fileList }) => {
		// Check if there are any files selected
		if (fileList.length > 0) {
			const selectedFile = fileList[0].originFileObj; // Get the actual file object
			setFile(selectedFile);
		} else {
			setFile(null); // Reset if no files are selected
		}
	};

	const handleUploadClick = async () => {
		if (file) {
			try {
				// Call the service to upload the profile picture
				const response = await uploadProfileImage(userId, file);

				// This updates the profile picture
				onProfileUpdated({
					name: name,
					email: email,
					phone: phone,
					profilePic: `${response.profilePic
						}?timestamp=${Date.now()}`,
				}); // The timestamp query parameter is set to tell the browser to
				//refetch the profile picture eventhough they have the same name

				setIsModalVisible(false); // Close modal after upload
			} catch (error) {
				console.error("Error uploading image:", error);
			}
		}
	};

	return (
		<div className="flex sm:flex-row flex-col items-center justify-between gap-6 bg-gray-100 p-6 rounded-lg shadow-md">
			<div className="flex items-center gap-6">
				<div className="relative">
					{updatedProfilePicture ? (
						<img
							src={updatedProfilePicture} // Use updated profile picture or fallback
							alt="Profile"
							className="w-28 h-28 rounded-full object-cover border-2 border-gray-300"
						/>
					) : (
						<div className="w-10 h-10 rounded-full shadow-lg ring-4 ring-blue-200 transform hover:scale-105 transition-transform duration-300">
							<UserOutlined />
						</div>
					)}
					<Button
						icon={<CameraOutlined />}
						className="absolute bottom-0 right-0 transform translate-x-1 translate-y-1"
						onClick={() => setIsModalVisible(true)}
						shape="circle"
						size="large"
						style={{
							backgroundColor: "blue",
							borderColor: "blue",
							color: "white",
						}}
					/>
				</div>
				<div className="flex flex-col">
					<h2 className="text-xl font-semibold text-gray-800">
						{name}
					</h2>
					<p className="text-gray-600">{email}</p>
					<p className="text-gray-600">
						{phone || "No phone number provided."}
					</p>
				</div>
			</div>
			<button
				className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
				onClick={() => setIsEditing(true)}
			>
				Edit Profile
			</button>

			{/* Modal for Profile Image Upload */}

			<Modal
				title="Upload Profile Picture"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				footer={null}
				centered
			>
				<Upload
					accept="image/*"
					// showUploadList={false}
					onChange={handleFileChange}
					beforeUpload={(file) => {
						const isImage = file.type.startsWith("image/");
						if (!isImage) {
							message.error("You can only upload image files!");
						}
						return !isImage;
					}}
				>
					<Button icon={<UploadOutlined />} type="primary" block>
						Select Image
					</Button>
				</Upload>
				<div className="flex justify-end mt-4">
					<Button type="primary" onClick={handleUploadClick}>
						Upload
					</Button>
				</div>
			</Modal>

			{isEditing && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white p-8 rounded-lg max-w-lg w-full shadow-lg">
						<h3 className="text-xl font-semibold mb-4 text-center">
							Edit My Profile
						</h3>
						<form className="flex flex-col gap-4">
							<label className="flex flex-col">
								Name:
								<input
									type="text"
									value={updatedName}
									onChange={(e) =>
										setUpdatedName(e.target.value)
									}
									className="mt-1 p-2 border rounded-md"
								/>
							</label>
							<label className="flex flex-col">
								Email:
								<input
									type="email"
									value={updatedEmail}
									onChange={(e) =>
										setUpdatedEmail(e.target.value)
									}
									className="mt-1 p-2 border rounded-md"
								/>
							</label>
							<label className="flex flex-col">
								Phone:
								<input
									type="tel" // Input type for phone numbers
									value={updatedPhone}
									onChange={(e) =>
										setUpdatedPhone(e.target.value)
									}
									className="mt-1 p-2 border rounded-md"
								/>
							</label>

							<div className="flex justify-center gap-4 mt-4">
								<button
									type="button"
									className={`bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition ${loading
										? "opacity-50 cursor-not-allowed"
										: ""
										}`}
									onClick={handleSave}
									disabled={loading} // Disable button while loading
								>
									{loading ? "Saving..." : "Save"}
								</button>
								<button
									type="button"
									className="bg-red-600 text-white py-2 px-6 rounded-md hover:bg-red-700 transition"
									onClick={() => setIsEditing(false)}
								>
									Cancel
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default ProfileHeader;
